import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError, delay } from 'rxjs/operators';

import { MatSnackBar } from '@angular/material/snack-bar';
import { Environment } from '../classes/environment';

import { ReportingYear, ReportingYearData, ReportingYearMemberLink } from 'src/app/classes/reportingData';

@Injectable({
	providedIn: 'root'
})
export class DataService {

	constructor(
		private _http: HttpClient,
		@Inject('environment') private environment: Environment,
		private matSnackBar: MatSnackBar,
	) {}

	getReportingYearData( reportingYearId: string, memberId: string ): Observable<ReportingYearData[]> {

		// console.log( 'getReportingYearData memberId=' + memberId, 'reportingYearId=' + reportingYearId )
		if( !reportingYearId || !memberId ) return of([]);
      
		const params = new HttpParams().set( 'bjJahr', reportingYearId ).set( 'mitgliedId', memberId );
    
		return this._http.get<any>( 
			this.environment.backendUrl + 'daten/search', 
			{ params: params } 
		).pipe( 
			map( o => {
				let daten = [];
				if( o && o.daten ) daten = o.daten;
				return daten;
			}),
			catchError(err => {
				console.log( 'An error occurred while fetching the data', err );
				this.matSnackBar.open( 'Beim Abrufen der Daten ist ein Fehler aufgetreten', '', { duration: 5000 } );
				// return throwError(err);
				return of([]);
			})
		);

	}

	getAllReportingYearsLink( memberId: string ): Observable<ReportingYearMemberLink[]> {
		if( !memberId ) return of( [] );

		return this._http.get<ReportingYearMemberLink[]>( 
			this.environment.backendUrl + 'daten/mitglied/all/' + memberId )
		.pipe( 
			map( rymls => {
				// console.log( 'getAllReportingYearsData', rymls )
				// const rymlsPublished = rymls.filter( r => r.)
				return rymls;
			}),
			catchError(err => {
				console.log( 'An error occurred while fetching the data', err );
				this.matSnackBar.open( 'Beim Abrufen der Daten ist ein Fehler aufgetreten', '', { duration: 5000 } );
				return of( [] );
			})
		);
	}

	getAllPublishedReportingYearsData( memberId: string ): Observable< Map<string, ReportingYearData[]> > {
		const rydMap = new Map<string, ReportingYearData[]>();
		if( !memberId ) return of( rydMap );

		return this._http.get<ReportingYearMemberLink[]>( 
			this.environment.backendUrl + 'daten/mitglied/' + memberId )
		.pipe( 
			map( rymls => {
				for( const ryml of rymls ) {
					console.log( 'year', ryml.bjJahr )
					rydMap.set( ryml.bjJahr, ryml.daten );
				}
				return rydMap;
			}),
			catchError(err => {
				console.log( 'An error occurred while fetching the data', err );
				this.matSnackBar.open( 'Beim Abrufen der Daten ist ein Fehler aufgetreten', '', { duration: 5000 } );
				return of( rydMap );
			})
		);
	}


	getComparisonData( bjJahr: string, mitgliedIds: string[] ): Observable<ReportingYearMemberLink[]> {
		if( !bjJahr || !mitgliedIds || !mitgliedIds.length ) return of([]);
      
		const params = new HttpParams()
			.set( 'bjJahr', bjJahr )
			.set( 'mitgliedIds', mitgliedIds.toString() );
    
		return this._http.get<ReportingYearMemberLink[]>(
			this.environment.backendUrl + 'daten/compare', 
			{ params: params }
		).pipe( 
			map( arr => {
				return arr;
			}),
			catchError(err => {
				console.log( 'An error occurred while fetching the data', err );
				this.matSnackBar.open( 'Beim Abrufen der Daten ist ein Fehler aufgetreten', '', { duration: 5000 } );
				return of([]);
			})
		);
	}


	saveReportingYearData( ryd: ReportingYearData ): Observable<boolean> {
		return ( ryd.bmdId ) ?
			this.updateReportingYearData( ryd ) :
			this.createReportingYearData( ryd );
	}

	private updateReportingYearData( ryd: ReportingYearData ): Observable<boolean> {

		return this._http.put<{ daten: ReportingYear, verarbeitet: boolean }>(
			this.environment.backendUrl + 'daten/update',
			ryd
		).pipe(
			// delay( 5000 ),
			map( response => {
				if( !response || !response.verarbeitet ) {
					console.log( 'Updating the data failed', response );
					this.matSnackBar.open( 'Das Aktualisieren der Daten ist fehlgeschlagen', '', { duration: 5000 } );
					return false;
				}
				console.log( 'Data update successful' );
				this.matSnackBar.open( 'Datenaktualisierung erfolgreich', '', { duration: 5000 } );
				return true;
			}),
			catchError( error => {
				console.log( 'An error occurred while updating the data', error );
				this.matSnackBar.open( 'Beim Aktualisieren der Daten ist ein Fehler aufgetreten', '', { duration: 5000 } );
				return of(false);
			})
		);
	}

	private createReportingYearData( ryd: ReportingYearData ): Observable<boolean> {

		return this._http.post<{ daten: ReportingYearData, verarbeitet: boolean }>(
			this.environment.backendUrl + 'daten/new',
			ryd
		).pipe(
			map( response => {
				if( !response || !response.verarbeitet ) {
					console.log( 'Creating the data failed', response );
					this.matSnackBar.open( 'Das Erstellen der Daten ist fehlgeschlagen', '', { duration: 5000 } );
					return false;
				}
				console.log( 'Data creation successful' );
				this.matSnackBar.open( 'Datenerstellung erfolgreich', '', { duration: 5000 } );
				ryd.bmdId = response.daten.bmdId;
				return true;
			}),
			catchError( error => {
				console.log( 'An error occurred while creating the data', error );
				this.matSnackBar.open( 'Beim Erstellen der Daten ist ein Fehler aufgetreten', '', { duration: 5000 } );
				return of(false);
			})
		);
	}

	createReportingYearMemberLink( year: string, mitgliedId: string ): Observable<ReportingYearMemberLink> {

		const ryml = new ReportingYearMemberLink();
		ryml.bjJahr = year;
		ryml.mitgliedId = mitgliedId;

		return this._http.post<{ bjm: ReportingYearMemberLink, verarbeitet: boolean }>(
			this.environment.backendUrl + 'bjm/new',
			ryml
		).pipe(
			map( response => {
				if( !response || !response.verarbeitet ) {
					console.log( 'Creating the ReportingYearMemberLink failed', response );
					this.matSnackBar.open( 'Das Erstellen der Daten ist fehlgeschlagen', '', { duration: 5000 } );
					return new ReportingYearMemberLink();
				}
				console.log( 'ReportingYearMemberLink creation successful' );
				this.matSnackBar.open( 'Datenerstellung erfolgreich', '', { duration: 5000 } );
				return response.bjm;
			}),
			catchError( error => {
				console.log( 'An error occurred while creating the ReportingYearMemberLink', error );
				this.matSnackBar.open( 'Beim Erstellen der Daten ist ein Fehler aufgetreten', '', { duration: 5000 } );
				return of( new ReportingYearMemberLink() );
			})
		);
	}

	updateReportingYearMemberLink( ryml: ReportingYearMemberLink ): Observable<boolean> {

		return this._http.put<{ bjm: ReportingYearMemberLink, verarbeitet: boolean }>(
			this.environment.backendUrl + 'bjm/update',
			ryml
		).pipe(
			map( response => {
				if( !response || !response.verarbeitet ) {
					console.log( 'Updating the ReportingYearMemberLink failed', response );
					this.matSnackBar.open( 'Beim Aktualisieren der Daten ist fehlgeschlagen', '', { duration: 5000 } );
					return false;
				}
				console.log( 'ReportingYearMemberLink update successful' );
				this.matSnackBar.open( 'Datenaktualisierung erfolgreich', '', { duration: 5000 } );
				return true;
			}),
			catchError( error => {
				console.log( 'An error occurred while updating the ReportingYearMemberLink', error );
				this.matSnackBar.open( 'Beim Aktualisieren der Daten ist ein Fehler aufgetreten', '', { duration: 5000 } );
				return of( false );
			})
		);

	}

}